import { DateRange } from 'react-day-picker'

import { useApolloClient } from '@apollo/client/react/hooks/useApolloClient'
import {
  FiltersDefinition,
  FiltersState,
  OneDataCollection,
  useDataCollectionSource,
} from '@factorialco/f0-react/dist/experimental'
import {
  AnalyticsTraceabilityResultsDocument,
  AnalyticsTraceabilityResultsQuery,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

import { datatableFiltersToJsonLogicFilters } from 'modules/ai_reports/components/DatasourceTypeRenderer/filters'
import { formatColumns } from 'modules/ai_reports/components/DatasourceTypeRenderer/utils'

import type { ReportTableAction, ReportTableProps } from './index'

interface TraceabilityTableContentProps {
  id: string
  sqlQuery: string
  tableId: string
  actions?: {
    primary?: ReportTableAction
    secondary?: ReportTableAction[]
  }
  columnFormatHints: ReportTableProps['columnFormatHints']
  onStateChange: ReportTableProps['onStateChange']
  dateFilter?: DateRange
  onUserFacingSqlQueryChange: (sqlQuery: string) => void
  columnsData: AnalyticsTraceabilityResultsQuery
  filterDefinitions: FiltersDefinition<string>
  currentFilters: FiltersState<FiltersDefinition<string>> | undefined
}

const INITIAL_PAGE = 1
const INITIAL_PAGE_SIZE = 10

const TraceabilityTableContent = ({
  id,
  sqlQuery,
  tableId,
  actions,
  columnFormatHints,
  onStateChange,
  dateFilter,
  onUserFacingSqlQueryChange,
  columnsData,
  filterDefinitions,
  currentFilters,
}: TraceabilityTableContentProps) => {
  const client = useApolloClient()
  const { company, access } = useCurrentSession()

  const source = useDataCollectionSource(
    {
      filters: filterDefinitions,
      currentFilters: currentFilters,
      primaryActions: () => actions?.primary,
      secondaryActions: actions?.secondary
        ? {
            expanded: 1,
            actions: () => actions.secondary || [],
          }
        : undefined,
      dataAdapter: {
        paginationType: 'pages',
        perPage: INITIAL_PAGE_SIZE,
        fetchData: (options) => {
          const pagination = options.pagination
          const perPage = pagination.perPage ?? INITIAL_PAGE_SIZE
          const currentPage = pagination.currentPage ?? INITIAL_PAGE

          const dateFrom = dateFilter?.from ?? moment().startOf('day').toDate()
          const dateTo = dateFilter?.to ?? moment().endOf('day').toDate()

          const normalizedFrom = moment(dateFrom).startOf('day').toDate()
          const normalizedTo = moment(dateTo).endOf('day').toDate()

          const filters = options.filters
          const jsonLogicFilters = datatableFiltersToJsonLogicFilters(filters)

          return client
            .watchQuery({
              query: AnalyticsTraceabilityResultsDocument,
              fetchPolicy: 'cache-and-network',
              variables: {
                query: sqlQuery,
                companyId: company.id,
                dateRange: {
                  from: normalizedFrom.toISOString(),
                  to: normalizedTo.toISOString(),
                },
                page: currentPage,
                perPage: perPage,
                tableId: tableId,
                filters: JSON.stringify(jsonLogicFilters),
              },
              notifyOnNetworkStatusChange: false,
              nextFetchPolicy: 'cache-and-network',
            })
            .map(({ data, loading, error }) => {
              onUserFacingSqlQueryChange(
                data?.iris?.traceabilityResultsConnection?.nodes?.[0]?.sqlQuery ?? ''
              )
              return {
                loading: loading,
                error: error,
                data: {
                  records:
                    data?.iris.traceabilityResultsConnection.nodes.map((node) =>
                      Object.fromEntries(node.values.map(({ name, value }) => [name, value ?? '']))
                    ) ?? [],
                  total: data?.iris.traceabilityResultsConnection.totalCount ?? 0,
                  currentPage: currentPage,
                  pagesCount: Math.ceil(
                    (data?.iris.traceabilityResultsConnection.totalCount ?? 0) / perPage
                  ),
                  perPage: perPage,
                  type: 'pages',
                },
              }
            })
        },
      },
    },
    [sqlQuery, company.id, dateFilter, filterDefinitions]
  )

  return (
    <OneDataCollection
      fullHeight
      source={source}
      id={`traceability-table-${id}/v1`}
      storage={false}
      onStateChange={onStateChange}
      visualizations={[
        {
          type: 'table',
          options: {
            allowColumnReordering: true,
            allowColumnHiding: true,
            frozenColumns: 1,
            columns: formatColumns(
              columnsData?.iris.traceabilityResultsConnection.nodes || [],
              columnFormatHints,
              access.locale
            ),
          },
        },
      ]}
    />
  )
}

export default TraceabilityTableContent
